import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { config } from "../../Environment";
import useRetryFetch from "../../hooks/useRetryFetch";
import { Button } from "../ui/button";
import {
  Loader2, Layers,
  Anchor, Activity, Award, BarChart2, Bike, BookOpen, Bookmark, Brain,
  Briefcase, Building2, Calendar, Camera, Car, Clock, Cloud, Code, Coffee,
  Compass, Cpu, Crown, Dumbbell, Eye, Film, Flame, Folder, FolderOpen,
  Globe, GraduationCap, Headphones, Heart, Home, Image, Key, Laptop,
  Leaf, Lightbulb, Lock, Map, MapPin, Medal, MessageCircle, MessageSquare,
  Mic, Moon, Mountain, Music, Package, Paintbrush, Palette, Pen, Plane,
  Rocket, Settings, Shield, ShoppingBag, Smile, Sparkles, Star, Sun, Tag,
  Target, Terminal, TrendingUp, Trophy, User, Users, Utensils, Wrench, Zap,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui/tooltip";
import { useDirection } from "../../hooks/useDirection";

const CATEGORY_ICON_MAP: Record<string, LucideIcon> = {
  Anchor, Activity, Award, BarChart2, Bike, BookOpen, Bookmark, Brain,
  Briefcase, Building2, Calendar, Camera, Car, Clock, Cloud, Code, Coffee,
  Compass, Cpu, Crown, Dumbbell, Eye, Film, Flame, Folder, FolderOpen,
  Globe, GraduationCap, Headphones, Heart, Home, Image, Key, Laptop, Layers,
  Leaf, Lightbulb, Lock, Map, MapPin, Medal, MessageCircle, MessageSquare,
  Mic, Moon, Mountain, Music, Package, Paintbrush, Palette, Pen, Plane,
  Rocket, Settings, Shield, ShoppingBag, Smile, Sparkles, Star, Sun, Tag,
  Target, Terminal, TrendingUp, Trophy, User, Users, Utensils, Wrench, Zap,
};

interface Category {
  _id: string;
  name: string | { en: string; he?: string; de?: string };
  slug: string;
  productCount: number;
  icon?: string;
}

interface CategorySidebarProps {
  selectedCategory?: string;
  onCategorySelect?: (categorySlug: string | null) => void;
  isMobile?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  totalProductsOverride?: number;
}

const CategorySidebar = ({
  selectedCategory,
  onCategorySelect,
  isMobile = false,
  onClose,
  collapsed = false,
  totalProductsOverride,
}: CategorySidebarProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { isRTL } = useDirection();
  const tooltipSide = isRTL ? ("left" as const) : ("right" as const);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getLocalizedName = (name: string | { en: string; he?: string; de?: string }): string => {
    if (typeof name === "string") return name;
    if (typeof name === "object" && name !== null) {
      const lang = i18n.language || "en";
      if (lang === "he" && name.he) return name.he;
      if (lang === "de" && name.de) return name.de;
      return name.en || "";
    }
    return "";
  };

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const query = new URLSearchParams();
      query.set("language", i18n.language || "en");
      const response = await retryFetch(
        `${config.API_BASE}/api/categories?${query.toString()}`,
        { method: "GET", credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to load categories");
      const data = await response.json();
      setCategories(data.list || []);
      setTotalProducts(data.totalProducts || 0);
    } catch (err) {
      setError(t("error_loading_categories") || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleCategoryClick = (categoryId: string) => {
    if (onCategorySelect) {
      onCategorySelect(categoryId);
    } else {
      const cat = categories.find((c) => c._id === categoryId);
      if (cat) navigate(`/marketplace/category/${cat.slug}`);
    }
    if (isMobile && onClose) onClose();
  };

  const handleClearCategory = () => {
    if (onCategorySelect) onCategorySelect(null);
    else navigate("/marketplace");
    if (isMobile && onClose) onClose();
  };

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center p-8", isMobile ? "min-h-[200px]" : "h-64")}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary/40" />
          <p className="text-xs text-gray-400">{t("loading") || "Loading..."}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-xs text-red-600 mb-3 p-3 bg-red-50 rounded-lg border border-red-200">{error}</div>
        <Button variant="outline" size="sm" onClick={loadCategories} className="w-full text-xs">
          {t("retry") || "Retry"}
        </Button>
      </div>
    );
  }

  const visibleCategories = categories.filter((c) => c.productCount > 0);
  const displayTotal = totalProductsOverride ?? totalProducts;

  /* ── Collapsed (icon-only) mode ─────────────────────────────────── */
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={200}>
        <div className="h-full py-3 flex flex-col items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleClearCategory}
                className={cn(
                  "w-9 h-9 rounded-xl flex items-center justify-center transition-all",
                  !selectedCategory
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-400 hover:text-gray-700 hover:bg-white/60"
                )}
              >
                <Layers className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>
              {t("all_products") || "All Products"} ({displayTotal})
            </TooltipContent>
          </Tooltip>

          {visibleCategories.length > 0 && (
            <>
              <div className="w-5 h-px bg-gray-200 my-1" />
              {visibleCategories.map((category) => {
                const name = getLocalizedName(category.name);
                const initial = name.charAt(0).toUpperCase();
                const IconComp = category.icon ? CATEGORY_ICON_MAP[category.icon] : undefined;
                const isActive = selectedCategory === category._id;
                return (
                  <Tooltip key={category._id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleCategoryClick(category._id)}
                        className={cn(
                          "w-9 h-9 rounded-xl flex items-center justify-center transition-all text-xs font-semibold",
                          isActive
                            ? "bg-primary text-white shadow-sm"
                            : "text-gray-400 hover:text-gray-700 hover:bg-white/60"
                        )}
                      >
                        {IconComp ? <IconComp className="w-4 h-4" /> : initial}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side={tooltipSide}>
                      {name} ({category.productCount})
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </>
          )}
        </div>
      </TooltipProvider>
    );
  }

  /* ── Expanded (full) mode ────────────────────────────────────────── */
  return (
    <div className={cn(
      "h-full flex flex-col",
      "bg-gradient-to-b from-slate-50/80 to-white/60",
      isMobile && "rounded-xl border border-gray-200/70"
    )}>

      {/* Header */}
      <div className="px-4 pt-4 pb-2.5 border-b border-gray-100/80" style={{ background: "linear-gradient(to bottom, rgba(248,250,252,0.9), transparent)" }}>
        <p className="text-[10px] font-bold tracking-widest uppercase select-none" style={{ color: "hsl(var(--primary) / 0.45)" }}>
          Browse
        </p>
      </div>

      {/* All Products row */}
      <div className="px-3 pt-2">
        <button
          onClick={handleClearCategory}
          className={cn(
            "w-full text-start px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 flex items-center justify-between group",
            !selectedCategory
              ? "bg-primary text-white shadow-md shadow-primary/20"
              : "text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm"
          )}
        >
          <span className="flex items-center gap-2">
            <div className={cn(
              "w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-colors",
              !selectedCategory ? "bg-white/20" : "bg-gray-100 group-hover:bg-gray-200/60"
            )}>
              <Layers className={cn("w-3.5 h-3.5", !selectedCategory ? "text-white" : "text-gray-500")} />
            </div>
            <span className="truncate">{t("all_products") || "All Products"}</span>
          </span>
          <span className={cn(
            "ms-2 text-[11px] font-semibold tabular-nums shrink-0 px-1.5 py-0.5 rounded-md",
            !selectedCategory
              ? "bg-white/20 text-white"
              : "bg-gray-100 text-gray-500 group-hover:bg-gray-200/60"
          )}>
            {displayTotal}
          </span>
        </button>
      </div>

      {/* Divider + category label */}
      {visibleCategories.length > 0 && (
        <div className="px-4 pt-3 pb-1">
          <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-300 select-none">
            Categories
          </p>
        </div>
      )}

      {/* Category list */}
      <div className="px-3 pb-3 flex-1 overflow-y-auto space-y-0.5 scrollbar-thin">
        {visibleCategories.length === 0 ? (
          <div className="px-2 py-10 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
              <Layers className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500 font-medium mb-1">
              {t("no_categories") || "No categories yet"}
            </p>
            <p className="text-xs text-gray-400 leading-relaxed">
              {t("categories_appear_here") || "Categories will appear as products are added"}
            </p>
          </div>
        ) : (
          visibleCategories.map((category) => {
            const IconComp = category.icon ? CATEGORY_ICON_MAP[category.icon] : undefined;
            const name = getLocalizedName(category.name);
            const isActive = selectedCategory === category._id;
            return (
              <button
                key={category._id}
                onClick={() => handleCategoryClick(category._id)}
                className={cn(
                  "w-full text-start px-3 py-2.5 rounded-xl text-sm transition-all duration-150 flex items-center justify-between group",
                  isActive
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-gray-600 font-medium hover:text-gray-900 hover:bg-white hover:shadow-sm"
                )}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <div className={cn(
                    "w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                    isActive
                      ? "bg-primary/15 text-primary"
                      : "bg-gray-100 text-gray-400 group-hover:bg-gray-200/60 group-hover:text-gray-600"
                  )}>
                    {IconComp
                      ? <IconComp className="w-3.5 h-3.5" />
                      : <span className="text-[10px] font-bold">{name.charAt(0).toUpperCase()}</span>
                    }
                  </div>
                  <span className="truncate">{name}</span>
                </span>
                <span className={cn(
                  "ms-2 text-[11px] font-semibold tabular-nums shrink-0 px-1.5 py-0.5 rounded-md transition-colors",
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "bg-gray-100 text-gray-400 group-hover:bg-gray-200/60 group-hover:text-gray-600"
                )}>
                  {category.productCount}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CategorySidebar;
